// Hand-maintained queue contracts. Keep the generated SDK unchanged.
import { client } from "./client.gen";
import type { WorkRecordSummary } from "./types.gen";

export type AuditorQueueBucket = "today" | "done" | "needs_fix" | "parked";
export type AuditorQueueReason =
  | "evidence_broken"
  | "handled_elsewhere"
  | "wrong_dealer_or_duplicate"
  | "legacy_ignored"
  | "broken_audit"
  | "previously_flagged"
  | "superseded"
  | "other";
export type AuditorReviewOrder = "oldest_first" | "newest_first";
/** Effective settings of one Work Operations program; null goal or window is off. */
export interface AuditorQueueProgramSettings {
  program: string;
  queue_enabled: boolean;
  daily_target: number | null;
  sla_hours: number | null;
  focus_enabled: boolean;
  qc_enabled: boolean;
  review_order: AuditorReviewOrder;
  /** Team-calendar date, YYYY-MM-DD. */
  baseline_date: string | null;
  /** Midnight of baseline_date in the current team timezone. */
  baseline_at: string | null;
  customized: boolean;
  updated_by: string | null;
  updated_at: string | null;
  /** Today and Focus mode list TDDS resubmissions first. */
  resubmissions_first: boolean;
}
/** The selected program's settings and the team day they count in. */
export interface AuditorQueueSettings {
  program: string | null;
  daily_target: number | null;
  sla_hours: number | null;
  focus_enabled: boolean | null;
  qc_enabled: boolean | null;
  review_order: AuditorReviewOrder;
  baseline_date: string | null;
  baseline_at: string | null;
  timezone: string;
}
export type AuditorQueueProgramSettingsPatch = Partial<
  Pick<
    AuditorQueueProgramSettings,
    | "queue_enabled"
    | "daily_target"
    | "sla_hours"
    | "focus_enabled"
    | "qc_enabled"
    | "review_order"
    | "resubmissions_first"
    | "baseline_date"
  >
>;
export interface AuditorQueueSettingsResponse {
  timezone: string;
  programs: AuditorQueueProgramSettings[];
  leads: string[];
}
export interface AuditorQueueHold {
  kind: "flagged" | "parked";
  reason: AuditorQueueReason;
  note: string | null;
  held_by: string | null;
  held_at: string;
}
/** Latest saved finding decision on a record not yet signed off. */
export interface AuditorQueueReviewProgress {
  reviewer_id: string;
  last_decision_at: string;
  decided_items: number;
}
export interface AuditorQueueRecord extends WorkRecordSummary {
  record_label: string;
  entered_at: string;
  entered_estimated: boolean;
  due_at: string | null;
  hold: AuditorQueueHold | null;
  review_progress: AuditorQueueReviewProgress | null;
  parked_reason: string | null;
  queue_completed_at: string | null;
  completed_by: string | null;
}
export interface AuditorQueueSummary {
  settings: AuditorQueueSettings;
  counts: {
    today: number;
    overdue: number;
    in_review_by_others: number;
    reviewed_by_others_awaiting_signoff: number;
    needs_fix: number;
    parked: number;
    flagged_by_me: number;
    done: number;
    team_completed: number;
    mine_completed: number;
    overdue_cleared: number;
    on_time: number;
  };
  day_start: string;
  day_end: string;
  best_day: { date: string; count: number } | null;
}
export interface AuditorWorkRecordQueueResponse {
  records: AuditorQueueRecord[];
  /** Null when no program in the organization has its queue on. */
  summary: AuditorQueueSummary | null;
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  viewer_is_review_admin: boolean;
  viewer_id: string;
  program: string | null;
  programs: AuditorQueueProgramSettings[];
}
export interface AuditorQueueFilters {
  bucket: AuditorQueueBucket;
  program?: string;
  search?: string;
  only_mine?: boolean;
  /** true: only TDDS resubmissions; false: only records that are not. */
  resubmission?: boolean;
}
const security = [{ scheme: "bearer", type: "http" }] as const;
function unwrap<T>(response: { data?: unknown; error?: unknown }): T {
  if (response.error) {
    const error = response.error as { detail?: string };
    throw new Error(
      typeof error.detail === "string"
        ? error.detail
        : "The queue could not be updated. Please try again.",
    );
  }
  return response.data as T;
}
export async function listWorkRecordQueue(
  orgId: string,
  filters: AuditorQueueFilters,
  offset = 0,
  limit = 100,
) {
  return unwrap<AuditorWorkRecordQueueResponse>(
    await client.get({
      security: [...security],
      url: "/api/playbooks/work-records/queue",
      query: { org_id: orgId, ...filters, offset, limit },
    }),
  );
}
export async function getWorkRecordQueueSettings(orgId: string) {
  return unwrap<AuditorQueueSettingsResponse>(
    await client.get({
      security: [...security],
      url: "/api/playbooks/work-records/queue/settings",
      query: { org_id: orgId },
    }),
  );
}
export type AuditorQueueSettingsMutation =
  | {
      action: "program";
      program: string;
      changes: AuditorQueueProgramSettingsPatch;
    }
  | { action: "team"; timezone: string };
export async function mutateWorkRecordQueueSettings(
  orgId: string,
  mutation: AuditorQueueSettingsMutation,
) {
  const query = { org_id: orgId };
  if (mutation.action === "team")
    return unwrap<{ timezone: string }>(
      await client.put({
        security: [...security],
        url: "/api/playbooks/work-records/queue/settings",
        query,
        body: { timezone: mutation.timezone },
      }),
    );
  return unwrap<AuditorQueueProgramSettings>(
    await client.put({
      security: [...security],
      url: `/api/playbooks/work-records/queue/settings/${encodeURIComponent(mutation.program)}`,
      query,
      body: mutation.changes,
    }),
  );
}
export type AuditorQueueMutation =
  | { action: "flag"; id: string; reason: AuditorQueueReason; note?: string }
  | { action: "park"; ids: string[]; reason: AuditorQueueReason; note?: string }
  | { action: "release"; ids: string[] };
export async function mutateWorkRecordQueue(
  orgId: string,
  mutation: AuditorQueueMutation,
) {
  const query = { org_id: orgId };
  const url =
    mutation.action === "flag"
      ? `/api/playbooks/work-records/${mutation.id}/queue-flag`
      : `/api/playbooks/work-records/queue/${mutation.action}`;
  const body =
    mutation.action === "release"
      ? { ids: mutation.ids }
      : {
          ...(mutation.action === "park" ? { ids: mutation.ids } : {}),
          reason: mutation.reason,
          note: mutation.note,
        };
  return unwrap<{ affected: number }>(
    await client.post({ security: [...security], url, query, body }),
  );
}
